import React, { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Avatar, Callout, FormRow, Input, VbButton } from "@/components/vb";
import { useAuth } from "@/context/AuthContext";
import { resetPasswordSendEmail, updatePassword } from "@/services/authService";

/** Character classes the meter rewards variety across. */
const CHAR_CLASSES = [/[a-z]/, /[A-Z]/, /\d/, /[^A-Za-z0-9]/];

const STRENGTH_TONES = ["weak", "fair", "good", "strong"] as const;
const STRENGTH_LABELS = ["Weak", "Fair", "Good", "Strong"] as const;

/**
 * 0 for an empty box, otherwise 1–4. A point each for reaching 12 characters,
 * reaching 16, using three character classes and using all four — so the row's
 * own hint ("at least 12 characters") is what actually moves the meter. Floored
 * at 1 so anything typed fills a segment instead of showing a dead track.
 * Advisory only: Django's password validators are the real gate on submit.
 */
const scorePassword = (password: string): number => {
    if (!password) return 0;
    const classes = CHAR_CLASSES.filter((test) => test.test(password)).length;
    let score = 0;
    if (password.length >= 12) score += 1;
    if (password.length >= 16) score += 1;
    if (classes >= 3) score += 1;
    if (classes >= 4) score += 1;
    return Math.max(1, score);
};

const SettingsSecurity: React.FC = () => {
    const { user, token, logout } = useAuth();

    const [currentPassword, setCurrentPassword] = useState("");
    const [newPassword, setNewPassword] = useState("");
    const [confirmPassword, setConfirmPassword] = useState("");
    const [error, setError] = useState("");
    const [success, setSuccess] = useState("");
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [sendingReset, setSendingReset] = useState(false);

    // A successful change logs the user out 2s later. Held in a ref so leaving
    // the panel before it fires cancels it — otherwise the timer survives the
    // unmount and signs them out mid-way through another settings tab.
    const logoutTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
    useEffect(
        () => () => {
            if (logoutTimer.current) clearTimeout(logoutTimer.current);
        },
        [],
    );

    const displayName =
        `${user?.first_name ?? ""} ${user?.last_name ?? ""}`.trim() || (user?.email ?? "");
    const strength = scorePassword(newPassword);

    const handleChangePassword = async (e: React.FormEvent<HTMLFormElement>): Promise<void> => {
        e.preventDefault();
        setError("");
        setSuccess("");

        if (newPassword !== confirmPassword) {
            setError("New password and confirmation do not match.");
            return;
        }

        try {
            setIsSubmitting(true);
            const response = await updatePassword(token, currentPassword, newPassword);
            if (response.status === 204) {
                setSuccess("Password changed successfully. You will be logged out shortly.");
                logoutTimer.current = setTimeout(() => {
                    void logout();
                }, 2000);
            } else {
                setError("Failed to change password. Please try again.");
            }
        } catch {
            setError("An unexpected error occurred. Please try again.");
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleSendResetLink = async (): Promise<void> => {
        if (!user?.email || sendingReset) return;
        try {
            setSendingReset(true);
            await resetPasswordSendEmail(user.email);
            toast.success(`Reset link sent to ${user.email}`);
        } catch {
            toast.error("Failed to send the reset link. Please try again.");
        } finally {
            setSendingReset(false);
        }
    };

    return (
        <>
            <section className="vb-card">
                <div className="vb-card-hd">
                    <div className="vb-card-hd__left">
                        <h2>Signed in as</h2>
                        <p className="vb-card-hd__sub">
                            These credentials are yours, not the workspace&apos;s — they follow you
                            into every workspace you belong to.
                        </p>
                    </div>
                </div>

                <div className="vb-owner-field">
                    <Avatar size="sm" round kind="company" name={displayName} decorative />
                    <span className="vb-owner-field__nm">{displayName}</span>
                    {/* displayName falls back to the email when the account has no
                        first/last name — don't print the same string twice. */}
                    {displayName !== user?.email && (
                        <span className="vb-owner-field__em">{user?.email}</span>
                    )}
                </div>
            </section>

            {/* The <form> *is* the card: the header's submit button then sits inside
                it natively, and every row picks up .vb-card's 16px child gap. */}
            <form className="vb-card" onSubmit={handleChangePassword}>
                <div className="vb-card-hd">
                    <div className="vb-card-hd__left">
                        <h2>Password</h2>
                        <p className="vb-card-hd__sub">Use a strong, unique password.</p>
                    </div>
                    <VbButton type="submit" disabled={isSubmitting}>
                        {isSubmitting ? "Updating…" : "Update password"}
                    </VbButton>
                </div>

                {error && (
                    <Callout tone="danger" role="alert">
                        {error}
                    </Callout>
                )}
                {success && (
                    <Callout tone="success" role="status">
                        {success}
                    </Callout>
                )}

                <FormRow label="Current password" id="security-current-password">
                    <Input
                        type="password"
                        autoComplete="current-password"
                        value={currentPassword}
                        onChange={(event) => setCurrentPassword(event.target.value)}
                        required
                    />
                </FormRow>

                <FormRow
                    label="New password"
                    hint="At least 12 characters. Avoid reused passwords."
                    id="security-new-password"
                >
                    <Input
                        type="password"
                        autoComplete="new-password"
                        value={newPassword}
                        onChange={(event) => setNewPassword(event.target.value)}
                        required
                    />
                    {strength > 0 && (
                        <div className={`vb-strength vb-strength--${STRENGTH_TONES[strength - 1]}`}>
                            <div
                                className="vb-strength__track"
                                role="meter"
                                aria-valuenow={strength}
                                aria-valuemin={0}
                                aria-valuemax={4}
                                aria-valuetext={STRENGTH_LABELS[strength - 1]}
                                aria-label="Password strength"
                            >
                                {[0, 1, 2, 3].map((segment) => (
                                    <span
                                        key={segment}
                                        className={
                                            segment < strength
                                                ? "vb-strength__seg vb-strength__seg--on"
                                                : "vb-strength__seg"
                                        }
                                    />
                                ))}
                            </div>
                            <span className="vb-strength__label">
                                {STRENGTH_LABELS[strength - 1]}
                            </span>
                        </div>
                    )}
                </FormRow>

                <FormRow label="Confirm new password" id="security-confirm-password">
                    <Input
                        type="password"
                        autoComplete="new-password"
                        value={confirmPassword}
                        onChange={(event) => setConfirmPassword(event.target.value)}
                        required
                    />
                </FormRow>

                <div className="vb-card-ft">
                    <span className="text-[13.5px] font-medium text-vb-text-2">Forgot it?</span>
                    <button
                        type="button"
                        className="vb-textlink"
                        onClick={() => void handleSendResetLink()}
                        disabled={sendingReset || !user?.email}
                    >
                        {sendingReset ? "Sending…" : "Send me a reset link →"}
                    </button>
                </div>
            </form>
        </>
    );
};

export default SettingsSecurity;
